/**
 * DisputeDetailPage - Complete case expediente view
 *
 * DevStory 4.5: Sistema de resolución de disputas con participación de la DAO.
 * Implements Criterios 6, 7, 8, 9, 10, 11, 12.
 */

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Spinner, TabsLine } from '@components/ui';
import {
  useDispute,
  useDisputeArguments,
  useDisputeHistory,
  useDisputeMessages,
} from '@hooks/useDisputes';
import { useAuthStore } from '@/stores/authStore';
import { BackendError } from '@/api/adapter/client';
import { DisputeStatusBadge } from '@components/features/disputes/DisputeStatusBadge';
import { DisputeArgumentsList } from '@components/features/disputes/DisputeArgumentsList';
import { DisputeHistoryViewer } from '@components/features/disputes/DisputeHistoryViewer';
import { DisputeCommunicationPanel } from '@components/features/disputes/DisputeCommunicationPanel';
import { DisputeDaoResolutionCard } from '@components/features/disputes/DisputeDaoResolutionCard';

const DISPUTE_TABS = [
  { id: 'arguments', label: 'Arguments & Claims' },
  { id: 'history', label: 'History & Evidence' },
  { id: 'channel', label: 'Direct Communication Channel' },
  { id: 'governance', label: 'DAO Governance & Escrow' },
];

export default function DisputeDetailPage(): JSX.Element {
  const { projectId, milestoneId, disputeId } = useParams<{
    projectId?: string;
    milestoneId?: string;
    disputeId: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('arguments');

  const {
    data: dispute,
    isLoading: isLoadingDispute,
    isError: isErrorDispute,
  } = useDispute(disputeId);
  const account = useAuthStore((s) => s.session?.accountId);
  const argumentsQuery = useDisputeArguments(disputeId);
  const argumentsList = argumentsQuery.data ?? [];
  const historyQuery = useDisputeHistory(disputeId);
  const history = historyQuery.data ?? {
    deliverables: [],
    reviews: [],
    rejections: [],
    events: [],
  };
  const canWrite =
    !!account &&
    (account === history.project?.clientId ||
      account === history.project?.consultantId);
  const messagesQuery = useDisputeMessages(
    disputeId,
    !!account && activeTab === 'channel'
  );
  const messages = messagesQuery.data?.messages ?? [];
  const channelDenied =
    !account ||
    (messagesQuery.error instanceof BackendError &&
      messagesQuery.error.statusCode === 404);

  const effectiveProjectId = projectId || dispute?.projectId;
  const contextMatches =
    (!projectId || projectId === dispute?.projectId) &&
    (!milestoneId || milestoneId === dispute?.milestoneId);

  if (isLoadingDispute) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--base-surface-1,#141414)]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isErrorDispute || !dispute || !contextMatches) {
    return (
      <div className="min-h-screen bg-[var(--base-surface-1,#141414)] px-8 py-10 lg:px-[112px]">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-1 text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))] hover:text-[var(--text-dark-primary,#f5f5f5)]"
        >
          <i className="ri-arrow-left-s-line text-lg" />
          Back
        </button>
        <div className="py-16 text-center">
          <i className="ri-error-warning-line mb-4 text-4xl text-rose-400" />
          <h2 className="mb-2 text-xl font-semibold text-[var(--text-dark-primary,#f5f5f5)]">
            Dispute Not Found
          </h2>
          <p className="mb-6 text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
            The dispute file could not be found or you do not have permission to
            view it.
          </p>
          <button
            onClick={() => navigate('/projects')}
            className="text-sm text-[var(--state-brand-active,#36d399)] hover:underline"
          >
            Back to projects
          </button>
        </div>
      </div>
    );
  }

  const isClosed =
    dispute.status === 'RESOLVED' ||
    dispute.status === 'REJECTED' ||
    dispute.status === 'CANCELLED';

  return (
    <div className="min-h-screen bg-[var(--base-surface-1,#141414)]">
      <div className="mx-auto max-w-7xl space-y-6 px-6 py-8 lg:px-[112px]">
        {/* Back navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              if (effectiveProjectId) {
                navigate(`/projects/${effectiveProjectId}`);
              } else {
                navigate(-1);
              }
            }}
            className="flex items-center gap-1 text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))] transition-colors hover:text-[var(--text-dark-primary,#f5f5f5)]"
          >
            <i className="ri-arrow-left-s-line text-lg" />
            Back to Project
          </button>

          <span className="font-mono text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.5))]">
            ID: {dispute.id}
          </span>
        </div>

        {/* Case Header Card */}
        <div className="space-y-4 rounded-2xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-6 shadow-lg">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-[var(--text-dark-primary,#f5f5f5)]">
                  Dispute Case File
                </h1>
                <DisputeStatusBadge status={dispute.status} size="md" />
              </div>

              <p className="text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                Project:{' '}
                {effectiveProjectId ? (
                  <Link
                    to={`/projects/${effectiveProjectId}`}
                    className="font-semibold text-[var(--state-brand-active,#36d399)] hover:underline"
                  >
                    {history.project?.title || effectiveProjectId}
                  </Link>
                ) : (
                  <span>{history.project?.title || 'Project'}</span>
                )}{' '}
                · Milestone:{' '}
                <span className="font-semibold text-white">
                  {history.milestone?.title || dispute.milestoneId}
                </span>
              </p>
            </div>

            <div className="flex flex-col space-y-1 text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))] md:items-end">
              <div>
                <span>Opened on: </span>
                <strong className="text-white">
                  {new Date(dispute.createdAt).toLocaleString()}
                </strong>
              </div>
              <div>
                <span>Initiated by: </span>
                <span className="font-semibold text-amber-300">
                  {dispute.openedBy?.name ||
                    dispute.openedByUserRole ||
                    'Dispute Initiator'}
                </span>
              </div>
            </div>
          </div>

          {/* Initial claim highlight */}
          <div className="space-y-1 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-300">
              <i className="ri-quote-text" /> Initial Opening Argument &
              Justification:
            </div>
            <p className="whitespace-pre-wrap pl-5 text-sm italic leading-relaxed text-white">
              &quot;{dispute.openingReason}&quot;
            </p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-[var(--base-border,#3d3d3d)]">
          <TabsLine
            tabs={DISPUTE_TABS}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </div>

        {/* Tab Content */}
        <div className="pt-2">
          {activeTab === 'arguments' && argumentsQuery.isLoading && <Spinner />}
          {activeTab === 'arguments' && argumentsQuery.error && (
            <p role="alert">{argumentsQuery.error.message}</p>
          )}
          {activeTab === 'arguments' &&
            !argumentsQuery.isLoading &&
            !argumentsQuery.error && (
              <DisputeArgumentsList
                dispute={dispute}
                argumentsList={argumentsList}
                isClosed={isClosed}
                canWrite={canWrite}
              />
            )}

          {activeTab === 'history' && historyQuery.isLoading && <Spinner />}
          {activeTab === 'history' && historyQuery.error && (
            <p role="alert">{historyQuery.error.message}</p>
          )}
          {activeTab === 'history' &&
            !historyQuery.isLoading &&
            !historyQuery.error && (
              <DisputeHistoryViewer dispute={dispute} history={history} />
            )}

          {activeTab === 'channel' && channelDenied && (
            <p>This channel is private to the client and coordinator.</p>
          )}
          {activeTab === 'channel' && !!account && messagesQuery.isLoading && (
            <Spinner />
          )}
          {activeTab === 'channel' && !channelDenied && messagesQuery.error && (
            <p role="alert">{messagesQuery.error.message}</p>
          )}
          {activeTab === 'channel' &&
            !channelDenied &&
            !messagesQuery.isLoading &&
            !messagesQuery.error && (
              <DisputeCommunicationPanel
                dispute={dispute}
                messages={messages}
                isClosed={isClosed}
                canWrite={messagesQuery.data?.canWrite ?? false}
              />
            )}

          {activeTab === 'governance' && (
            <DisputeDaoResolutionCard
              dispute={dispute}
              clientName={history.project?.clientName}
              professionalName={history.project?.consultantName}
            />
          )}
        </div>
      </div>
    </div>
  );
}
